import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output } from '@angular/core';
import { SpreadingState } from '@components/main/spreading/state/manager';
import { AutocompleteOption } from '@components/main/spreading/models/models';
import { debounce } from '../add-categorization-modal/utils/debounce';

const SEARCH_DEBOUNCE_MS = 250;

/**
 * Debounced search + single-select list of template items. Shared by the
 * recategorize modal and the Offset Adjustment view so both present the same
 * template-item picker (CITI-1274).
 */
@Component({
  selector: 'app-template-item-search-select',
  templateUrl: './template-item-search-select.component.html',
  styleUrls: ['./template-item-search-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TemplateItemSearchSelectComponent {
  @Input() state: SpreadingState;
  /** Template item to omit from the list (e.g. the current categorization). */
  @Input() excludeItemId: number | null = null;
  /** When true, list rollup/validation targets instead of mappable leaves. */
  @Input() validationOnly = false;
  /** Highlights the currently chosen item, when the caller tracks a selection. */
  @Input() selectedItemId: number | null = null;
  @Input() placeholder = 'Search template items...';

  @Output() itemSelected = new EventEmitter<AutocompleteOption>();

  searchQuery = '';
  debouncedSearchQuery = '';

  private readonly searchDebouncer = debounce<string>(SEARCH_DEBOUNCE_MS, (term: string) => {
    this.debouncedSearchQuery = term;
    this.cdr.detectChanges();
  });

  constructor(private cdr: ChangeDetectorRef, private el: ElementRef<HTMLElement>) {}

  get availableItems(): AutocompleteOption[] {
    if (!this.state) {
      return [];
    }
    const options = this.validationOnly
      ? this.state.rollupOptions
      : this.state.autocompleteOptions;
    return (options ?? []).filter((option) => option.id !== this.excludeItemId);
  }

  get filteredItems(): AutocompleteOption[] {
    const term = this.debouncedSearchQuery;
    if (!term) {
      return this.availableItems;
    }
    return this.availableItems.filter((option) =>
      (option.value ?? '').toLowerCase().includes(term)
    );
  }

  onSearchInput(value: string): void {
    this.searchQuery = value;
    const trimmed = (value || '').trim().toLowerCase();
    if (trimmed === '') {
      this.searchDebouncer.cancel();
      this.debouncedSearchQuery = '';
      this.cdr.detectChanges();
      return;
    }
    this.searchDebouncer.run(trimmed);
  }

  onItemClick(option: AutocompleteOption): void {
    if (option?.id == null) {
      return;
    }
    this.itemSelected.emit(option);
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.optionElements()[0]?.focus();
    }
  }

  onOptionKeydown(option: AutocompleteOption, event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onItemClick(option);
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }
    event.preventDefault();
    const options = this.optionElements();
    const currentIndex = options.indexOf(event.currentTarget as HTMLElement);
    if (event.key === 'ArrowDown') {
      options[Math.min(currentIndex + 1, options.length - 1)]?.focus();
    } else if (currentIndex <= 0) {
      this.focusSearchInput();
    } else {
      options[currentIndex - 1]?.focus();
    }
  }

  private optionElements(): HTMLElement[] {
    return Array.from(
      this.el.nativeElement.querySelectorAll<HTMLElement>('.source-item-left')
    );
  }

  private focusSearchInput(): void {
    this.el.nativeElement.querySelector<HTMLElement>('.source-item-search-input')?.focus();
  }
}
